import React, { useEffect, useState } from "react";
import GoalListItem from "../goalListItem/goalListItem";
import SectionToggle from "../sectionToggle/sectionToggle";
import MultiSelectDropdown from "../multiSelectDropdown/multiSelectDropdown";
import { useFilteredGoals } from "./hooks/useFilteredGoals";
import { useSortedGoals } from "./hooks/useSortedGoals";
import { ArrowIcon } from "../../../assets/images/svg/index";

const FilterDropdown = ({
  className,
  label,
  options,
  selected,
  placeholder,
  hideFilter,
  onChange,
  ...restProps
}) => (
  <div className={className}>
    {!hideFilter && (
      <>
        <p>{label}</p>
        <MultiSelectDropdown
          options={options}
          selectedFilters={selected}
          placeholder={placeholder}
          includeSelectAll
          numberDisplayed={0}
          enableOnly
          haveOnSelectChange
          onSelectChange={onChange}
          {...restProps}
        />
      </>
    )}
  </div>
);

const getValues = (data, valueName, maxDisplay = 10000) => {
  const values = [];
  data?.forEach((value, index) => {
    const title = value[valueName];
    if (index < maxDisplay && !values.includes(title)) {
      values.push(title);
    }
  });
  return values;
};

const GoalsList = ({
  selectedView,
  isCurrentMonth,
  isAnimated,
  goalData,
  customClass,
  zIndex,
  goalLoading,
  date,
  title,
  saveGoalAsValue,
  singleLocation,
  canModifyGoals,
  saveGoal
}) => {
  if (!goalData) return null;

  const isTotalView = selectedView === "total";
  const visibleColumns = {
    showLocation: !isTotalView && !singleLocation,
    showIncrease: !isTotalView,
    showForecast: isCurrentMonth,
    showNeeded: isCurrentMonth,
    showDisplayDropdown: !isTotalView,
    showModifyButton: isCurrentMonth && !isTotalView
  };
  const locationOptions = getValues(goalData, "locationName");
  const metricOptions = getValues(goalData, "title");
  const [selectedMetrics, setSelectedMetrics] = useState(metricOptions);
  const [selectedLocations, setSelectedLocations] = useState(locationOptions.slice(0, 5));
  const [selectedDisplayed, setSelectedDisplayed] = useState(["ACTIVE", "INACTIVE"]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });

  const filteredGoals = useFilteredGoals(
    goalData,
    selectedLocations,
    selectedMetrics,
    selectedDisplayed,
    isTotalView
  );

  const sortedGoals = useSortedGoals(filteredGoals, sortConfig);
  const [visibleGoals, setVisibleGoals] = useState(sortedGoals || []);

  useEffect(() => {
    const scrollY = window.scrollY;
    const html = document.documentElement;
    document.documentElement.style.scrollBehavior = "auto";

    setVisibleGoals(sortedGoals);

    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY });
      html.style.scrollBehavior = "smooth";
    });
  }, [sortedGoals]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "desc" };
      if (prev.direction === "desc") return { key, direction: "asc" };

      return { key: null, direction: null };
    });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key === key)
      return sortConfig.direction === "asc" ? (
        <ArrowIcon className="sorting-icon up" />
      ) : (
        <ArrowIcon className="sorting-icon down" />
      );
  };

  const SortableColumn = ({ className, label, sortKey, condition = true }) => {
    if (!condition) return null;
    return (
      <p className={className}>
        <span className="sortable-column" onClick={() => handleSort(sortKey)}>
          {label}
          <span style={{ marginLeft: "3px" }}>{getSortArrow(sortKey)}</span>
        </span>
      </p>
    );
  };

  return (
    <div style={{ marginTop: "7px" }}>
      <SectionToggle
        title={title}
        isLoading={goalLoading}
        customClass={customClass}
        autoHeight
        content={
          <div className="goals-list-wrapper" style={{ zIndex }}>
            <div className="row-headers">
              {!singleLocation && (
                <FilterDropdown
                  hideFilter={!visibleColumns.showLocation}
                  className="region"
                  label="Location"
                  options={locationOptions}
                  selected={selectedLocations}
                  placeholder="Site"
                  onChange={setSelectedLocations}
                />
              )}
              <FilterDropdown
                className="site"
                label="Metric"
                options={metricOptions}
                selected={selectedMetrics}
                placeholder="Goal"
                onChange={setSelectedMetrics}
              />
              {visibleColumns.showIncrease && <p className="goal">Increase</p>}
              <SortableColumn className="goal" label="Goal" sortKey="goalValue" />
              <SortableColumn
                className="current"
                label={isTotalView ? "Total" : "Current"}
                sortKey="currentVal"
              />
              <SortableColumn
                className="forecast"
                label="Forecast"
                sortKey="currentValPacing"
                condition={visibleColumns.showForecast}
              />
              <SortableColumn
                className="needed"
                label="Needed"
                sortKey="needed"
                condition={visibleColumns.showNeeded}
              />
              <SortableColumn className="pacing" label="Progress" sortKey="progress" />
              {visibleColumns.showDisplayDropdown && (
                <FilterDropdown
                  className="show-goal"
                  label="Display"
                  options={["ACTIVE", "INACTIVE"]}
                  selected={selectedDisplayed}
                  placeholder="Show Goal"
                  onChange={setSelectedDisplayed}
                  includeSelectAll
                  numberDisplayed={2}
                  enableOnly
                  haveOnSelectChange
                />
              )}
            </div>
            <div>
              {visibleGoals.map((value) => {
                const { metricType, goalObjectId, id } = value;
                return (
                  <GoalListItem
                    isAnimated={isAnimated}
                    goalObjectId={goalObjectId}
                    key={id ?? metricType}
                    date={date}
                    singleLocation={singleLocation}
                    canModifyGoals={canModifyGoals}
                    visibleColumns={visibleColumns}
                    metricType={metricType}
                    saveGoal={saveGoal}
                    saveGoalAsValue={saveGoalAsValue}
                    goalData={value}
                  />
                );
              })}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default GoalsList;
